import React, { useCallback, useState } from 'react';
import { Text, Alert, AlertIcon } from '@chakra-ui/react';

import {
  DateInputWithConfirm,
  TextareaWithConfirm,
  UploadActions,
  UploadExample,
  getStringFromDate,
  getDateFromString,
  UploadComplete as UploadCompleteSection,
  ModalColumns,
  Link,
  RequirementsSummary,
  RequirementsRemedy,
  RadioOptionsWithConfirm,
  IQuestion
} from 'shared-components';

import placeholderExample from '../../../../../assets/images/sample-biometric-residence-permit.jpg';
import { BiometricResidencePermitDoc, IDate } from '../../../../../types';
import { validateDate } from '../../../../../utilities/validateDate';

enum SelectionOptions {
  YES = 'Yes',
  NOT_YET = 'Not yet',
  WILL_NOT_RECEIVING = 'I will not be receiving a BRP card'
}

interface IProps {
  closeModal: () => void;
  openUploadModal: (
    docKey: string,
    docType: string,
    sponsorType: string,
    sponsorKey: string | null,
    modalTitle: string,
    fileNames: string[],
    metadata?: any
  ) => void;
  biometricResidencePermit: BiometricResidencePermitDoc;
  docType: string;
  sponsorType: string;
  isComplete: boolean;
  docKey: string;
  saveFollowUpDocumentsBRPWillNotReceiving: (docKey: string, reason: string) => void;
}

export const BiometricResidencePermitModal = (props: IProps) => {
  const {
    closeModal,
    openUploadModal: initOpenUploadModal,
    saveFollowUpDocumentsBRPWillNotReceiving,
    docKey,
    isComplete: isDocumentApproved,
    sponsorType,
    docType,
    biometricResidencePermit
  } = props;
  const { fileNames, dateOfIssue: initDateOfIssue, untilDate: initUntilDate } = biometricResidencePermit;
  const isCompletedWillNotReceivedModal = isDocumentApproved && !fileNames.length;

  const title = 'Biometric Residence Permit';

  const [selectedStatus, setSelectedStatus] = useState<SelectionOptions | undefined>(
    isCompletedWillNotReceivedModal ? SelectionOptions.WILL_NOT_RECEIVING : SelectionOptions.YES
  );

  const [isChanged, setIsChanged] = useState<boolean>(false);

  const openUploadModal = (metadata: any) => {
    initOpenUploadModal(docKey, docType, sponsorType, null, title, fileNames, metadata);
  };

  const [dateOfIssue, setDateOfIssue] = useState(getDateFromString(initDateOfIssue));
  const [dateOfIssueError, setDateOfIssueError] = useState('');

  const [willNotReceivingReason, setWillNotReceivingReason] = useState(
    isCompletedWillNotReceivedModal ? biometricResidencePermit.notBeReceivingReason! : ''
  );

  const [untilDate, setUntilDate] = useState(getDateFromString(initUntilDate));
  const [untilDateError, setUntilDateError] = useState('');

  const checkDate = useCallback((date: IDate) => {
    return date.DD?.length && date.MM?.length && date.YYYY?.length === 4;
  }, []);

  const handleClick = (question: IQuestion) => {
    if (question.label !== selectedStatus) {
      setSelectedStatus(question.label as any);
      setIsChanged(true);
    }
  };

  const returnToSection = () => {
    closeModal();
  };

  const uploadDocument = () => {
    switch (selectedStatus) {
      case SelectionOptions.YES: {
        return openUploadModal({
          dateOfIssue: getStringFromDate(dateOfIssue),
          untilDate: getStringFromDate(untilDate)
        });
      }
      case SelectionOptions.WILL_NOT_RECEIVING: {
        return saveFollowUpDocumentsBRPWillNotReceiving(docKey, willNotReceivingReason);
      }
    }
  };

  const isComplete = (): boolean => {
    switch (selectedStatus) {
      case SelectionOptions.NOT_YET: {
        return true;
      }
      case SelectionOptions.YES: {
        return Boolean(
          !dateOfIssueError.length && !untilDateError.length && checkDate(dateOfIssue) && checkDate(untilDate)
        );
      }
      case SelectionOptions.WILL_NOT_RECEIVING: {
        return isChanged && Boolean(willNotReceivingReason.length);
      }
      default: {
        return false;
      }
    }
  };

  return (
    <ModalColumns
      id={'biometricResidencePermit'}
      title={title}
      onClose={closeModal}
      supportingColumn={<UploadExample altText="Biometric residence permit example" url={placeholderExample} />}
    >
      {isDocumentApproved && !isCompletedWillNotReceivedModal ? (
        <UploadCompleteSection fileNames={fileNames} onUpload={uploadDocument} onContinue={returnToSection} />
      ) : (
        <>
          <IntroWrapper />

          <RadioOptionsWithConfirm
            key={'selectedStatus'}
            value={selectedStatus}
            label={'Have you received your Biometric Residence Permit yet?'}
            onClick={handleClick}
            isInline={false}
            questions={[
              {
                char: '1',
                label: SelectionOptions.YES,
                selected: selectedStatus === SelectionOptions.YES
              },
              {
                char: '2',
                label: SelectionOptions.NOT_YET,
                selected: selectedStatus === SelectionOptions.NOT_YET
              },
              {
                char: '3',
                label: SelectionOptions.WILL_NOT_RECEIVING,
                selected: selectedStatus === SelectionOptions.WILL_NOT_RECEIVING
              }
            ]}
          />

          {selectedStatus === SelectionOptions.YES && (
            <>
              <DateInputWithConfirm
                id={`date-of-issue`}
                label={`What is the date of issue?`}
                subText={`The date will be under "place and date of issue" on the card`}
                value={dateOfIssue}
                errorText={dateOfIssueError}
                onChangeDD={(evt: any) => {
                  setDateOfIssue({
                    ...dateOfIssue,
                    DD: evt.target.value
                  });
                }}
                onChangeMM={(evt: any) => {
                  setDateOfIssue({
                    ...dateOfIssue,
                    MM: evt.target.value
                  });
                }}
                onChangeYY={(evt: any) => {
                  setDateOfIssue({
                    ...dateOfIssue,
                    YYYY: evt.target.value
                  });
                }}
                onBlur={() => setDateOfIssueError(validateDate(dateOfIssue))}
              />

              <DateInputWithConfirm
                id={`until-date`}
                label={`What is the valid until date?`}
                subText={`The date will be under "valid until" on the card`}
                value={untilDate}
                errorText={untilDateError}
                onChangeDD={(evt: any) => {
                  setUntilDate({
                    ...untilDate,
                    DD: evt.target.value
                  });
                }}
                onChangeMM={(evt: any) => {
                  setUntilDate({
                    ...untilDate,
                    MM: evt.target.value
                  });
                }}
                onChangeYY={(evt: any) => {
                  setUntilDate({
                    ...untilDate,
                    YYYY: evt.target.value
                  });
                }}
                onBlur={() => setUntilDateError(validateDate(untilDate))}
              />
            </>
          )}

          {selectedStatus === SelectionOptions.WILL_NOT_RECEIVING && (
            <>
              <TextareaWithConfirm
                key={'willNotReceivingReason'}
                id={'willNotReceivingReason'}
                value={willNotReceivingReason}
                label={`Please explain why you do not need a BRP`}
                onChange={(evt: React.FormEvent<HTMLInputElement>) => {
                  setWillNotReceivingReason(evt.currentTarget.value);
                  setIsChanged(true);
                }}
              />

              <Alert status="success" mb={6}>
                <AlertIcon />
                This item will be marked complete.
              </Alert>
            </>
          )}

          {selectedStatus === SelectionOptions.NOT_YET && (
            <RequirementsRemedy>
              <Text>
                If you have not received your permit, please come back to this page to upload it once you have.
              </Text>
            </RequirementsRemedy>
          )}

          {selectedStatus !== undefined && (
            <UploadActions
              complete={selectedStatus !== SelectionOptions.NOT_YET}
              label={selectedStatus === SelectionOptions.WILL_NOT_RECEIVING ? 'Finish' : undefined}
              disabled={!isComplete()}
              onContinue={selectedStatus === SelectionOptions.NOT_YET ? returnToSection : uploadDocument}
            />
          )}
        </>
      )}
    </ModalColumns>
  );
};

const IntroWrapper = () => {
  return (
    <RequirementsSummary title="Collect your BPR card once you're in the UK">
      <Text>
        You must usually do this before the visa vignette sticker in you travel document expires or within 10 days of
        arriving in the UK, whichever is later.
      </Text>

      <Text>For more information click the following link:</Text>

      <Text>
        <Link href={'https://www.gov.uk/biometric-residence-permits/collect'} isExternal>
          BRP Guidance from UKVI
        </Link>
      </Text>

      <Text mb={0}>Once collected, please return to this page to upload a scan/photo of both the front and back.</Text>
    </RequirementsSummary>
  );
};
